/**
 * Normalises the backdrop tone of product photography.
 *
 * The catalogue mixes three kinds of shot: studio rings on a neutral backdrop
 * that ranges from rgb(222) to rgb(255) depending on who shot it, lifestyle
 * crops where the "background" is skin, and one ring photographed against a
 * bright sky. In a grid they read as four different shops.
 *
 * CSS alone cannot fix this - it cannot see what is in the image. So we look:
 * a 40px copy of each card image is sampled at its four corners, and the card
 * is classified from that.
 *
 *   studio    near-white and unsaturated -> brightness-normalised so every
 *             backdrop lands on the same tone as the card surface
 *   tinted    bright but saturated (the sky) -> normalised and desaturated so
 *             it recedes instead of shouting
 *   lifestyle anything else, including every skin-tone crop -> left alone,
 *             because a hand is the subject, not a backdrop to be removed
 *
 * The brightness factor is clamped to +/-12%, so a misclassified image can
 * only ever shift slightly. Anything that fails - CORS, a decode error, no
 * canvas - leaves the card exactly as it was.
 */
(function () {
  "use strict";

  /* Luminance of the card surface, the tone every studio backdrop is aimed at.
     Read from the token rather than hard-coded, so retuning the palette
     retunes this with it. */
  function surfaceLuma() {
    var v = getComputedStyle(document.documentElement).getPropertyValue("--c-surface").trim();
    var m = v.match(/#([0-9a-f]{6})/i);
    if (!m) return 241;
    var n = parseInt(m[1], 16);
    return 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255);
  }

  var TARGET = surfaceLuma();
  var done = new WeakSet();

  function classify(px) {
    // px: four [r,g,b] corner samples.
    var lum = 0, sat = 0, warm = 0;
    px.forEach(function (p) {
      lum += 0.2126 * p[0] + 0.7152 * p[1] + 0.0722 * p[2];
      sat = Math.max(sat, Math.max.apply(null, p) - Math.min.apply(null, p));
      warm += p[0] - p[2]; // red over blue: skin and warm sweeps are positive
    });
    lum /= px.length;

    /* A backdrop, not a subject.

       The allowance used to stop at 24, which caught neutral and warm-cream
       sweeps but let every pale COLOURED ground through untreated - and this
       catalogue is full of them: pale blue, lilac, cyan. Eight cards in a row,
       eight different grounds, which is exactly the "reads as scraped" problem.

       It now runs to 42. Skin crops sit at 45 and up with a much lower
       luminance, so raising the ceiling while keeping the 195 floor still
       leaves clear air between a backdrop and a hand. */
    if (lum >= 195 && sat <= 42) return { kind: "studio", lum: lum, sat: sat };

    /* A coloured backdrop, which on this catalogue means exactly one thing: the
       ring shot against a bright sky. It has to be bright, saturated AND cool.
       Without the cool test this also caught the warm lifestyle crops, and
       desaturating a photograph of a hand in warm light kills the shot - there
       the colour is the subject, not a backdrop to be muted. */
    if (lum >= 150 && sat >= 40 && warm < 0) return { kind: "tinted", lum: lum };

    return { kind: "lifestyle", lum: lum };
  }

  function sample(url) {
    return new Promise(function (resolve, reject) {
      var probe = new Image();
      probe.crossOrigin = "anonymous";
      probe.decoding = "async";
      probe.onload = function () {
        try {
          var c = document.createElement("canvas");
          c.width = probe.naturalWidth;
          c.height = probe.naturalHeight;
          if (!c.width || !c.height) return reject();
          var x = c.getContext("2d", { willReadFrequently: true });
          x.drawImage(probe, 0, 0);
          var w = c.width - 1, h = c.height - 1;
          var pick = function (a, b) { var d = x.getImageData(a, b, 1, 1).data; return [d[0], d[1], d[2]]; };
          resolve({ corners: [pick(0, 0), pick(w, 0), pick(0, h), pick(w, h)] });
        } catch (e) {
          reject(e);
        }
      };
      probe.onerror = reject;
      // A 40px copy: enough to read a backdrop, small enough to be free.
      probe.src = url.replace(/([?&])width=\d+/, "$1width=40");
    });
  }

  function treat(media) {
    if (done.has(media)) return;
    done.add(media);
    var img = media.querySelector("img");
    if (!img) return;
    var url = img.currentSrc || img.src;
    if (!url || url.indexOf("width=") === -1) return;

    sample(url)
      .then(function (s) {
        /* The bottom-right corner used to be sampled separately to decide
           whether the large editorial label over the image should be ink or
           paper. That label is gone — the mood word is an eyebrow above the
           title now and nothing renders over the photograph — so the extra
           sample and the data-ink attribute it set have gone with it. Nothing
           read data-ink but that one rule. */
        var r = classify(s.corners);
        if (r.kind === "lifestyle") return;
        // Clamped: a wrong guess can only ever nudge, never wreck.
        var b = Math.max(0.88, Math.min(1.12, TARGET / r.lum));
        media.style.setProperty("--tone-b", b.toFixed(3));

        /* Brightness alone lands every backdrop on the same VALUE but leaves
           its hue, so a row of corrected cards is still pale blue next to
           lilac next to cream. A proportional desaturation pulls the coloured
           ones toward the neutral ones - scaled by how coloured they are, so a
           genuinely neutral sweep is left completely alone. */
        var sat = r.sat || 0;
        var desat = sat <= 12 ? 1 : Math.max(0.35, 1 - (sat - 12) / 46);
        media.style.setProperty("--tone-s", desat.toFixed(3));

        media.setAttribute("data-tone", r.kind);
      })
      .catch(function () {
        /* No treatment. The card still has one ratio, one fit and one surface,
           which is most of the benefit. */
      });
  }

  function scan(root) {
    (root || document).querySelectorAll(".ccard__media").forEach(function (m) {
      var img = m.querySelector("img");
      if (!img) return;
      if (img.complete) treat(m);
      else img.addEventListener("load", function () { treat(m); }, { once: true });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { scan(); });
  } else {
    scan();
  }

  // Grids that paginate, filter or load more replace their cards wholesale.
  if ("MutationObserver" in window) {
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType === 1) scan(n);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener("shopify:section:load", function (e) { scan(e.target); });
})();
